import {
  createBlockId,
  EXAM_BLOCK_LABELS,
  GAME_MODE_CATALOG,
  validateExam,
  type EvaluationExam,
  type GameMode,
} from '@interactive-room';
import type { AICaseRecipe } from './constants/subjectCatalog';
import { buildStudioLaunchUrl } from './constants/subjectCatalog';
import { isStandaloneActivityMode } from './constants/standaloneActivityModes';
import { buildStudioMaterialUrl } from './constants/studioMaterialRoutes';
import { createExamId, saveEvaluationExam } from './examStorage';

/**
 * Modos nativos (música, dictado, anatomía…) sin editor `/studio/:mode`.
 * Se materializan como bloque único dentro de una Clase interactiva en ExamBuilder.
 */
export function recipeUsesGuideBlockEditor(recipe: AICaseRecipe): boolean {
  return !isStandaloneActivityMode(recipe.mode);
}

export function saveRecipeAsWrappedExam(params: {
  recipe: AICaseRecipe;
  title: string;
  payload: unknown;
  subjectTag: string;
  courseId?: string;
}): EvaluationExam {
  const mode = params.recipe.mode as GameMode;
  const now = Date.now();
  const raw: EvaluationExam = {
    id: createExamId(),
    teacherId: 'teacher',
    title: params.title,
    subjectTag: params.subjectTag,
    courseId: params.courseId,
    resourceType: 'exam',
    deliveryFormat: 'interactive',
    deliveryProfile: 'interactive_class',
    createdAt: now,
    updatedAt: now,
    settings: {
      shuffleBlocks: false,
      allowRejoin: true,
      integrityMode: 'FLEXIBLE',
      advanceMode: 'HOST',
    },
    blocks: [
      {
        id: createBlockId(),
        index: 0,
        mode,
        label: EXAM_BLOCK_LABELS[mode] ?? GAME_MODE_CATALOG[mode]?.displayName ?? params.recipe.title,
        payload: params.payload as EvaluationExam['blocks'][0]['payload'],
      },
    ],
  };
  return saveEvaluationExam(validateExam(raw));
}

/** @deprecated Usar saveRecipeAsWrappedExam */
export const saveRecipeAsGuideExam = saveRecipeAsWrappedExam;

export function buildRecipePostGeneratePath(
  recipe: AICaseRecipe,
  opts: { draftId?: string; wrappedExamId?: string; guideExamId?: string },
): string {
  const examId = opts.wrappedExamId ?? opts.guideExamId;
  if (recipeUsesGuideBlockEditor(recipe) && examId) {
    return buildStudioMaterialUrl({
      profile: 'interactive_class',
      subjectId: recipe.subjectId,
      examId,
    });
  }
  return buildStudioLaunchUrl(recipe, opts.draftId);
}
